import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Brand } from "@/components/Brand";

export default function RatePage() {
  const [params] = useSearchParams();
  const [ticketId, setTicketId] = useState(params.get("ticket") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [score, setScore] = useState(Number(params.get("score")) || 0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    if (ticketId && email) {
      api.get("/tickets/lookup", { params: { ticket_id: ticketId, contact: email } }).then((r) => setTicket(r.data)).catch(() => setTicket(null));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!score) return toast.error("Tap a star to rate us first");
    setBusy(true);
    try {
      await api.post("/tickets/rate", { ticket_id: ticketId.trim(), contact: email.trim(), score, comment: review });
      setDone(score);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBEB] grain">
      <header className="border-b-2 border-black bg-[#FACC15]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Brand subtitle="Support Desk" />
          <Link to="/" className="rounded-full border-2 border-black bg-white px-3 py-1.5 text-sm font-bold hover:bg-[#FEF9C3]" data-testid="rate-back-link">Raise a query</Link>
        </div>
      </header>
      <main className="mx-auto max-w-xl px-4 py-10 sm:px-6">
        {done ? (
          <section className="brut-card brut-shadow p-8 text-center fade-up" data-testid="rate-thanks">
            <CheckCircle2 className="mx-auto h-14 w-14" />
            <h1 className="mt-4 font-display text-3xl font-extrabold">Thank you!</h1>
            <p className="mt-2 text-base text-zinc-700">You rated our support {done}/5 on {ticketId.toUpperCase()}. Your review helps us get better every day.</p>
          </section>
        ) : (
          <form onSubmit={submit} className="brut-card brut-shadow space-y-6 p-6 sm:p-8 fade-up" data-testid="rate-form">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">Rate our support</p>
              <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">How did we do?</h1>
              {ticket && <p className="mt-2 text-sm text-zinc-600" data-testid="rate-ticket-summary">{ticket.ticket_id} · {ticket.product} · {ticket.query}{ticket.handled_by ? ` · handled by ${ticket.handled_by}` : ""}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="field-label">Ticket No</label>
                <input className="field-input font-mono uppercase" value={ticketId} onChange={(e) => setTicketId(e.target.value)} required placeholder="VS-0001" data-testid="rate-ticket-input" />
              </div>
              <div>
                <label className="field-label">Email used on the ticket</label>
                <input type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="rate-email-input" />
              </div>
            </div>
            <div>
              <label className="field-label">Your rating</label>
              <div className="flex gap-1" onMouseLeave={() => setHover(0)} data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setScore(n)} onMouseEnter={() => setHover(n)} className="rounded-md p-1 transition-transform hover:scale-110" aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`rating-star-${n}`}>
                    <Star className={`h-10 w-10 ${n <= (hover || score) ? "fill-[#FACC15] text-black" : "text-zinc-400"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="field-label">Write a review</label>
              <textarea className="field-input min-h-[120px]" value={review} maxLength={2000} onChange={(e) => setReview(e.target.value)} placeholder="Tell us what went well or what we could do better…" data-testid="rate-review-input" />
            </div>
            <button type="submit" className="btn-dark w-full" disabled={busy} data-testid="rate-submit-button">{busy ? "Sending…" : "Submit rating & review"}</button>
          </form>
        )}
      </main>
    </div>
  );
}
